import { Award } from 'lucide-react';

export interface Badge {
  id: number;
  name: string;
  description: string;
  icon: string;
  color: string;
  earned: boolean;
  progress?: {
    current: number;
    total: number;
  };
}

interface UserBadgesProps {
  badges: Badge[];
}

const UserBadges = ({ badges = [] }: UserBadgesProps) => {
  const earnedBadges = badges.filter(badge => badge.earned);
  const inProgressBadges = badges.filter(badge => !badge.earned);
  
  return (
    <div className="vibebops-card">
      <div className="flex items-center gap-2 mb-4">
        <Award className="h-5 w-5 text-vibebops-accent" />
        <h3 className="font-semibold">Badges</h3>
      </div>
      
      {badges.length === 0 ? (
        <div className="py-4 text-center text-vibebops-textMuted">
          <p>No badges yet! Complete activities to earn badges.</p>
        </div>
      ) : (
        <div>
          <h4 className="text-sm font-medium mb-3">Earned ({earnedBadges.length})</h4>
          <div className="grid grid-cols-4 gap-2 mb-6">
            {earnedBadges.length === 0 ? (
              <div className="col-span-4 py-2 text-center text-vibebops-textMuted">
                No badges earned yet
              </div>
            ) : (
              earnedBadges.map((badge) => (
                <div 
                  key={badge.id} 
                  className="flex flex-col items-center p-2 rounded-lg bg-vibebops-highlight hover:bg-vibebops-highlight/70 transition-colors"
                  title={`${badge.name}: ${badge.description}`}
                >
                  <div 
                    className={`h-10 w-10 rounded-full mb-1 flex items-center justify-center text-white ${badge.color}`}
                  >
                    {badge.icon}
                  </div>
                  <span className="text-xs text-center truncate w-full">{badge.name}</span>
                </div>
              ))
            )}
          </div>
          
          <h4 className="text-sm font-medium mb-3">In Progress ({inProgressBadges.length})</h4>
          <div className="space-y-3">
            {inProgressBadges.length === 0 ? (
              <div className="py-2 text-center text-vibebops-textMuted">
                No badges in progress
              </div>
            ) : (
              inProgressBadges.slice(0, 3).map((badge) => (
                <div key={badge.id} className="flex items-center gap-3 p-2 rounded-lg bg-vibebops-highlight">
                  <div 
                    className={`h-10 w-10 rounded-full flex items-center justify-center text-white/50 ${badge.color}`}
                  >
                    {badge.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{badge.name}</p>
                    <p className="text-xs text-vibebops-textMuted truncate">{badge.description}</p>
                    {badge.progress && (
                      <div className="w-full h-1.5 bg-vibebops-background rounded-full mt-1 overflow-hidden">
                        <div 
                          className="h-full bg-vibebops-accent"
                          style={{ width: `${(badge.progress.current / badge.progress.total) * 100}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-vibebops-textMuted whitespace-nowrap">
                    {badge.progress ? `${badge.progress.current}/${badge.progress.total}` : ''}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBadges;
